import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelToDisplay, TimeBucketUnits } from '@iotv/datamodel';
import { isNonNaNNumber, SortOrder } from '@iotv/iotv-v3-types';
import { Alert, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { ObjectViewType } from '../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { StripedDataGrid } from '../../factories/DataGrid';
import { ObservationsContext } from '../Contexts/OvesrvationsContextType_V2';
import { UseObservationsContext } from '../Contexts/UseObservationsContext';
import { ObservationInputMetaKeys } from '../fns';
import { dateTimeFormatter, dateTimeFormatterCompact, DefaultCellRendererWithApi, getBestUnit } from '../gridColFns';
import { metricItemsToVOBs } from './helperFns';
import { matchViaBuckets } from './matchViaBuckets';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { useTimeBucketSelector } from '../../../hooks/useTimeBucketSelector';
import { MatchedReadingsContext } from '../Contexts/MatchedReadingsContext';
import { matchRawObservations } from './matchRawObservation';
import { BinaryChoiceRadio } from '../../common/BinaryChoice';
const debug = process.env.REACT_APP_DEBUG && false;
export const MatchedReadingsDataGrid = (props) => {
    const { state: ObservationContextState, dispatch } = useContext(ObservationsContext);
    const { persistedObservationHistory, save, deleteObject, working, querying, errMessage } = useContext(UseObservationsContext);
    const { observationHistory, objectHistory, bucketStrategyMap } = ObservationContextState;
    const { state: [timeBucketDef, setTimeBucketDef], components: bucketComponents, } = useTimeBucketSelector({ unit: TimeBucketUnits.MINUTE, width: 5 });
    const { state: matchedReadingsState, dispatch: matchedReadingsDispatch } = useContext(MatchedReadingsContext);
    const { bucketedHistory, matchedHistory, bufferMins, matchType } = matchedReadingsState;
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const dataFormatFn = isSmallView ? dateTimeFormatterCompact : dateTimeFormatter;
    const observationHistoryStore = observationHistory?.observedHistory ?? null;
    debug && console.log('historyStore', observationHistoryStore);
    useEffect(() => {
        if (matchType === 'AutoBucket') {
            if (observationHistory && objectHistory?.recentStateHistory) {
                const bucketedHistory = matchViaBuckets({
                    timeBucketDef,
                    recentStateHistory: objectHistory.recentStateHistory,
                    observationHistory,
                    actualReadingPrefix: 'actual_',
                    bucketStrategyMap,
                });
                matchedReadingsDispatch({ bucketedHistory, matchedHistory: null });
            }
        }
    }, [observationHistory, objectHistory, bucketStrategyMap, timeBucketDef, matchType]);
    useEffect(() => {
        if (matchType === 'MatchHistoryStore') {
            if (observationHistory && objectHistory?.recentStateHistory) {
                if (['fromTimestampMs', 'toTimestampMs'].every((k) => observationHistory.observedHistory?.keys().includes(k))) {
                    const matchedHistory = matchRawObservations({
                        recentStateHistory: objectHistory.recentStateHistory,
                        observationHistory,
                    });
                    matchedReadingsDispatch({ bucketedHistory: null, matchedHistory });
                }
                else {
                    console.log(`TEMP: Cannot match via MatchHistoryStore because observationHistory is missing required keys. observationHistory keys: ${observationHistory.observedHistory.keys().join(', ')}`);
                    matchedReadingsDispatch({ bucketedHistory: null, matchedHistory: null });
                }
            }
        }
    }, [observationHistory, objectHistory, matchType]);
    const bucketedKeys = matchType === 'AutoBucket'
        ? (bucketedHistory?.keys() ?? []).filter((k) => !ObservationInputMetaKeys.includes(k))
        : (matchedHistory?.keys().filter((k) => !ObservationInputMetaKeys.includes(k)) ?? []);
    const observatinStateSnapshots = matchType === 'AutoBucket'
        ? bucketedHistory
            ? bucketedHistory.getStateSnapshotArray(SortOrder.DESCENDING)
            : []
        : matchedHistory
            ? matchedHistory.getStateSnapshotArray(SortOrder.DESCENDING)
            : [];
    const pseudoTypeId = `bucketed_observation`;
    const observationMetricItems = persistedObservationHistory
        ? metricItemsToVOBs({
            psuedoParentVob: persistedObservationHistory,
            pseudoTypeId,
            stateSnapshots: observatinStateSnapshots,
        })
        : [];
    const handleBufferMinsChange = (e) => {
        const newVal = parseInt(e.target.value);
        matchedReadingsDispatch({ bufferMins: Number.isNaN(newVal) ? 0 : newVal });
    };
    const rows = [...observationMetricItems].reverse(); // reverse to show most recent at top
    const autoBucketDurationValueGetter = (a, row, c) => {
        const from = row.timestamp;
        const idx = rows.findIndex((r) => r.id === row.id);
        const nextTimestamp = idx > 0 ? rows[idx - 1].timestamp : null;
        const to = nextTimestamp;
        if (isNonNaNNumber(from) && isNonNaNNumber(to)) {
            const durationMs = to - from;
            const unit = getBestUnit(durationMs);
            const value = durationMs / unit.ms;
            // Show as integer if possible, else 2 decimals
            const displayValue = Number.isInteger(value) ? value : value.toFixed(2);
            return `${displayValue} ${unit.label}`;
        }
        return 'N/A';
    };
    const matchedObservationDurationValueGetter = (a, row, c) => {
        const from = row.fromTimestampMs;
        const to = row.toTimestampMs;
        if (isNonNaNNumber(from) && isNonNaNNumber(to)) {
            const durationMs = to - from;
            const unit = getBestUnit(durationMs);
            const value = durationMs / unit.ms;
            // Show as integer if possible, else 2 decimals
            const displayValue = Number.isInteger(value) ? value : value.toFixed(2);
            return `${displayValue} ${unit.label}`;
        }
        return 'N/A';
    };
    const [columnVisibilityModel] = useColumnVisibilityForDataGrid({
        dataTableReference: 'Observations',
        objectTypeId: 'HistoryStore',
        viewType: ObjectViewType.List,
    });
    const autoBucketBaseCols = [
        {
            field: 'fromTimestampMs',
            headerName: 'From',
            flex: 1,
            renderCell: ({ row }) => dataFormatFn(row.timestamp),
            editable: false,
        },
        {
            field: 'toTimestampMs',
            headerName: 'To',
            flex: 1,
            renderCell: ({ row }) => {
                const idx = rows.findIndex((r) => r.id === row.id);
                const nextTimestamp = idx > 0 ? rows[idx - 1].timestamp : null;
                return dataFormatFn(nextTimestamp);
            },
            editable: false,
        },
        { field: '_duraction', headerName: 'Duration', width: 150, valueGetter: autoBucketDurationValueGetter },
    ];
    const observationMatchBaseCols = [
        {
            field: 'fromTimestampMs',
            headerName: 'From',
            flex: 1,
            renderCell: ({ row }) => dataFormatFn(row.fromTimestampMs),
            editable: false,
        },
        {
            field: 'toTimestampMs',
            headerName: 'To',
            flex: 1,
            renderCell: ({ row }) => {
                const idx = rows.findIndex((r) => r.id === row.id);
                const nextTimestamp = idx > 0 ? rows[idx - 1].fromTimestampMs : null;
                return dataFormatFn(nextTimestamp);
            },
            editable: false,
        },
        { field: '_duraction', headerName: 'Duration', width: 150, valueGetter: matchedObservationDurationValueGetter },
        { field: 'description', headerName: 'Description', flex: 1, editable: false },
    ];
    // Default passthrough renderer for DataGrid cells
    const dynamicCols = bucketedKeys.map((k) => {
        const colDef = {
            field: k,
            headerName: camelToDisplay(k),
            editable: false,
            flex: 1,
            renderCell: DefaultCellRendererWithApi,
            renderEditCell: undefined,
        };
        return colDef;
    });
    const selectedBaseCols = matchType === 'AutoBucket' ? autoBucketBaseCols : observationMatchBaseCols;
    const columns = [...selectedBaseCols, ...dynamicCols];
    const tableRowHeight = 24;
    const dataGridProps = {
        columns,
        columnHeaderHeight: tableRowHeight,
        rowHeight: tableRowHeight,
        rows,
        loading: querying,
        columnVisibilityModel,
        autoHeight: true,
        rowCount: 20,
        onProcessRowUpdateError: (e) => {
            console.log(e.message);
        },
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
    };
    const getOtherMatchType = () => {
        if (matchType === 'AutoBucket') {
            return 'MatchHistoryStore';
        }
        else {
            return 'AutoBucket';
        }
    };
    return (_jsxs(Stack, { gap: 1, width: '100%', children: [_jsxs(Stack, { direction: 'row', gap: 2, alignItems: 'center', children: [_jsx(BinaryChoiceRadio, { currentValue: matchType,
                        valueA: 'AutoBucket',
                        valueB: 'MatchHistoryStore',
                        labelA: 'Buckets',
                        labelB: 'Observations',
                        onChange: () => matchedReadingsDispatch({ matchType: getOtherMatchType() }) }), _jsx(Typography, { variant: "body1", children: 'Buffer (mins)' }), _jsx(TextField, { size: 'small', type: 'number', value: bufferMins, onChange: handleBufferMinsChange }), matchType === 'AutoBucket' ? (_jsxs(Stack, { direction: 'row', gap: 2, alignItems: 'center', children: [_jsx(Typography, { variant: "body1", children: "Readings Buckets" }), bucketComponents.width, bucketComponents.unit] })) : (_jsx(Stack, { direction: 'row', gap: 2, alignItems: 'center', children: _jsx(Alert, { severity: "info", children: "Matching against observations" }) }))] }), querying ? (_jsx(Skeleton, { variant: 'rounded', height: '8em' }, "MatchedGridSkeleton")) : (_jsx(StripedDataGrid, { ...dataGridProps }, 'MatchedGrid'))] }, 'MatchedGridStack'));
};
